import React, { useEffect, useMemo, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import errorFilledTokens from "@amzn/meridian-tokens/base/icon/error-filled";
import Card from "@amzn/meridian/card";
import { useLocation, useNavigate } from "react-router-dom";
import { WadmContainer, ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import { moveItemsToContainer, reportMissingItems } from "src/services/toteService";
import { addItemToContainer } from "src/services/containerService";
import {
  normalizeItemsForDisplay,
  buildMoveItemsRequest,
  buildAddItemRequest,
  convertWeight,
} from "./confirmQuantityHelpers";
import ContainerWeightSummary from "./ContainerWeightSummary";
import PillButton from "src/components/UI/PillButton";
import ItemCard from "./ItemCard";
import { useAppSelector } from "src/store/store";
import "src/components/pages/classification/classification.scss";
import "./toteItems.scss";

interface ConfirmQuantityLocationState {
  itemFlowContext: ItemFlowContext;
  container: WadmContainer;
  usingFor: ContainerUsingFor;
  isMissingReport?: boolean;
}

const ConfirmQuantityPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ConfirmQuantityLocationState | undefined;
  const { activeSiteCode } = useAppSelector((state) => state.user);

  const [quantityOverrides, setQuantityOverrides] = useState<Record<string, string>>({});
  const [weightOverrides, setWeightOverrides] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isMissingReport = state?.isMissingReport ?? false;
  const itemFlowContext = state?.itemFlowContext;
  const container = state?.container;

  const containerDisplayName =
    container?.containerDisplayName ?? container?.containerDisplayId ?? container?.id;

  const items = useMemo(
    () => (itemFlowContext ? normalizeItemsForDisplay(itemFlowContext) : []),
    [itemFlowContext],
  );

  // Reset scroll when keyboard closes on iOS
  useEffect(() => {
    const handleFocusOut = () => {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }), 150);
    };
    document.addEventListener("focusout", handleFocusOut);
    return () => document.removeEventListener("focusout", handleFocusOut);
  }, []);

  const quantityErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    items.forEach((item) => {
      const val = quantityOverrides[item.id];
      if (val === undefined) return;
      const qty = parseInt(val, 10);

      if (isNaN(qty) || qty <= 0) {
        errors[item.id] = "Please enter a valid quantity.";
      } else if (item.maxQuantity !== undefined && qty > item.maxQuantity) {
        errors[item.id] = `Only ${item.maxQuantity} available in the tote.`;
      }
    });
    return errors;
  }, [items, quantityOverrides]);

  const weightErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    items.forEach((item) => {
      const val = weightOverrides[item.id];
      if (val !== undefined && (isNaN(parseFloat(val)) || parseFloat(val) <= 0))
        errors[item.id] = "Please enter a valid weight.";
    });
    return errors;
  }, [items, weightOverrides]);

  const hasAnyErrors =
    Object.keys(quantityErrors).length > 0 || Object.keys(weightErrors).length > 0;

  // Total quantity = sum of overridden (or original) quantities
  const totalQuantity = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (parseInt(quantityOverrides[item.id] ?? item.defaultQuantity, 10) || 0),
        0,
      ),
    [items, quantityOverrides],
  );

  // Total weight = sum of overridden weights or calculated quantity × unit weight
  const totalWeight = useMemo(
    () =>
      items.reduce((sum, item) => {
        const qty = parseInt(quantityOverrides[item.id] ?? item.defaultQuantity, 10) || 0;
        const effectiveWeight =
          weightOverrides[item.id] !== undefined
            ? parseFloat(weightOverrides[item.id]) || 0
            : (item.unitWeight ?? 0) * qty;
        return sum + effectiveWeight;
      }, 0),
    [items, quantityOverrides, weightOverrides],
  );
  // Check if adding these items would exceed the container's max weight
  const isContainerOverweight = useMemo(() => {
    if (!container?.maxWeight || isMissingReport) return false;
    const containerWeightUnit = container.maxWeight.unit;
    const containerUsedWeight = container.usage?.weight?.weight ?? 0;

    const itemsToAddWeight = items.reduce((sum, item) => {
      const itemQty = parseInt(quantityOverrides[item.id] ?? item.defaultQuantity, 10) || 0;
      const overrideStr = weightOverrides[item.id];
      const weight =
        overrideStr !== undefined ? (parseFloat(overrideStr) || 0) : (item.unitWeight ?? 0) * itemQty;

      return (
        sum + convertWeight(weight, item.weightUnit ?? containerWeightUnit, containerWeightUnit)
      );
    }, 0);

    return containerUsedWeight + itemsToAddWeight > container.maxWeight.weight;
  }, [container, items, quantityOverrides, weightOverrides, isMissingReport]);

  const handleSubmit = async () => {
    if (isSubmitting || !state || !itemFlowContext) return;
    if (!isMissingReport && !container) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const params = {
        itemFlowContext,
        container,
        usingFor: state.usingFor,
        quantityOverrides,
        weightOverrides,
      };

      if (itemFlowContext.type === "classification" || itemFlowContext.type === "toteExtraItem") {
        await addItemToContainer(buildAddItemRequest({ ...params, siteName: activeSiteCode }));
      } else if (isMissingReport) {
        const { siteId, items } = buildMoveItemsRequest({ ...params, siteId: activeSiteCode });
        await reportMissingItems({ siteId, items });
      } else if (container) {
        await moveItemsToContainer(buildMoveItemsRequest({ ...params, siteId: activeSiteCode }));
      }
      navigate("/tote/addedToContainer", {
        state: {
          itemFlowContext,
          totalQuantity,
          totalWeight,
          weightUnit: items[0]?.weightUnit,
          allItemsProcessed:
            itemFlowContext.type === "tote"
              ? totalQuantity === itemFlowContext.totalItemQuantity
              : undefined,
          isMissingReport,
        },
      });
    } catch {
      setSubmitError(
        isMissingReport
          ? "Failed to report missing items. Please try again."
          : "Failed to add to container. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!state || !itemFlowContext || (!state.isMissingReport && !container)) {
    return (
      <Column
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
        spacingInset="500"
      >
        <Text type="h200">No data found</Text>
        <Text color="secondary">Please go back and select a container.</Text>
        <PillButton text="Go back" color="secondary" onClick={() => navigate(-1)} />
      </Column>
    );
  }

  return (
    <Column
      className="app-gradient-bg pwa-scrollable confirm-quantity-page"
      spacing="none"
      spacingInset="none none 600"
    >
      <Row alignmentVertical="center" spacing="200" spacingInset="500 400 300">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h200" color="primary">
          {isMissingReport ? "Report Missing Item" : "Confirm Quantity"}
        </Text>
      </Row>

      <Column spacing="400" spacingInset="none 500 500">
        {isMissingReport ? (
          <Text type="b300" color="secondary">
            Confirm that this item is not physically present in the tote. If only some units are
            missing, adjust the quantity below.
          </Text>
        ) : (
          <Text type="h300" color="primary">
            Add to {containerDisplayName}
          </Text>
        )}
        <ContainerWeightSummary
          totalQuantity={totalQuantity}
          totalWeight={totalWeight}
          weightUnit={items[0]?.weightUnit}
          isOverweight={isContainerOverweight}
        />
        {items.map((item) => {
          const qty = parseInt(quantityOverrides[item.id] ?? item.defaultQuantity, 10) || 0;
          const computedWeight =
            item.unitWeight !== undefined && qty > 0
              ? (qty * item.unitWeight).toFixed(3)
              : item.defaultWeight;
          return (
            <ItemCard
              key={item.id}
              item={item}
              quantity={quantityOverrides[item.id] ?? item.defaultQuantity}
              weight={weightOverrides[item.id] ?? computedWeight}
              onQuantityChange={(val) => {
                setQuantityOverrides((prev) => ({ ...prev, [item.id]: val }));
                if (item.unitWeight !== undefined) {
                  setWeightOverrides((prev) => {
                    const next = { ...prev };
                    delete next[item.id];
                    return next;
                  });
                }
              }}
              onWeightChange={(val) => setWeightOverrides((prev) => ({ ...prev, [item.id]: val }))}
              quantityError={quantityErrors[item.id]}
              weightError={weightErrors[item.id]}
            />
          );
        })}
      </Column>

      <Column className="asin-sticky-footer">
        {submitError && (
          <Text type="b200" color="error" alignment="center">
            {submitError}
          </Text>
        )}
        <Button
          type="primary"
          size="large"
          disabled={isSubmitting || hasAnyErrors || isContainerOverweight}
          onClick={handleSubmit}
        >
          {isSubmitting ? "SUBMITTING..." : isMissingReport ? "CONFIRM" : "ADD TO CONTAINER"}
        </Button>
      </Column>
    </Column>
  );
};

export default ConfirmQuantityPage;
